/**
 * Server-side pagination mixin for vue-good-table.
 *
 * Usage:
 * 1. Import and add to mixins array in the List*.vue component
 * 2. In the controller's filter(), call `this.enrichFiltersWithPagination()`
 *    before Request.do() and `this.applyServerPage(response)` in the success callback
 * 3. For filter-criteria changes (dropdowns, search inputs), call
 *    `this.filterFromFirstPage()` instead of `this.filter()` so pagination
 *    rewinds to page 1 and the user does not stay on a stale page.
 *
 * Backend contract note:
 * The backend `$facet` pipelines across hp-shield-crud treat `skip` as a
 * 1-indexed PAGE NUMBER, not an item offset. They compute the MongoDB
 * `$skip` as `(skip - 1) * limit` when `skip > 0`. So this mixin sends
 * `skip = currentPage`, matching what the working CrtListPatrolPoint
 * controller already did by hand.
 */
export default {
    data() {
        return {
            currentPage: 1,
            totalItems: 0,
        }
    },
    methods: {
        /**
         * Call this in the filter() success callback.
         * Handles both response shapes:
         *   - { results: [...], totalCount: N }             (flat)
         *   - { results: [{ paginatedResults, totalCount }] } (aggregated)
         */
        applyServerPage(response) {
            if (Array.isArray(response?.results) && response.results.length === 1 && response.results[0]?.paginatedResults) {
                this.items = response.results[0].paginatedResults || []
                const tc = response.results[0].totalCount
                this.totalItems = Array.isArray(tc) ? (tc[0]?.count || 0) : (tc || 0)
            } else {
                this.items = response?.results || []
                this.totalItems = response?.totalCount ?? response?.total ?? this.items.length
            }
        },

        /**
         * Injects skip/limit into the current filters object.
         * Call this before Request.do() in filter().
         * `skip` is the 1-indexed page number (backend contract).
         */
        enrichFiltersWithPagination() {
            const perPage = this.paginationOptions?.perPage || 10
            this.filters.skip = this.currentPage
            this.filters.limit = perPage
        },

        onPageChange(params) {
            this.currentPage = params.currentPage
            this.filter()
        },

        onPerPageChange(params) {
            this.paginationOptions.perPage = params.currentPerPage
            this.currentPage = 1
            this.filter()
        },

        /**
         * Resets pagination to page 1 and re-runs filter().
         * Use this from any filter-criteria change (dropdowns, search)
         * so the user cannot stay on a page number that no longer exists
         * in the new result set.
         */
        filterFromFirstPage() {
            this.currentPage = 1
            this.filter()
        },

        /**
         * Returns pagination options configured for server-side mode.
         */
        buildServerPaginationOptions(vue) {
            return {
                enabled: true,
                mode: 'pages',
                perPage: 10,
                position: 'bottom',
                perPageDropdown: [10, 20, 50, 100],
                dropdownAllowAll: false,
                setCurrentPage: 1,
                jumpFirstOrLast: true,
                firstLabel: vue.$t('str.table.pagination.first.page'),
                lastLabel: vue.$t('str.table.pagination.last.page'),
                nextLabel: vue.$t('str.table.pagination.next.page'),
                prevLabel: vue.$t('str.table.pagination.prev.page'),
                rowsPerPageLabel: vue.$t('str.table.pagination.rows.per.page.lavel'),
                ofLabel: vue.$t('str.table.pagination.of.label.page'),
                pageLabel: vue.$t('str.table.pagination.page'),
                allLabel: vue.$t('str.table.pagination.all.label'),
            }
        },
    },
}
